/* eslint-disable complexity */
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { DurchfuehrungsTag } from '@chancenwerk/_cms/products/entities/Product.js';
import { ProductLKK } from '@chancenwerk/_cms/products/entities/ProductLKK.js';
import '@chancenwerk/client/components/chancen-component-public-school-picker.js';
import '@chancenwerk/client/design/components/cr-button.js';
import '@chancenwerk/client/design/components/cr-flex.js';
import '@chancenwerk/client/design/components/cr-grid.js';
import '@chancenwerk/client/design/components/cr-input.js';
import '@chancenwerk/client/design/components/cr-tooltip.js';
import '@chancenwerk/components/semantics/chancen-component-semantic-partner-picker.js';
import type { LogistikCartItem } from '@chancenwerk/db/LogistikCart.js';
import { ChancenBriefVariableValue, type LogistikOrderChancenBriefItem } from '@chancenwerk/db/LogistikOrder.js';
import {
  possiblePrefillChancenBriefVariableNames,
  type ChancenBrief,
  type TravelCertificate,
} from '@chancenwerk/db/LogistikProduct.js';
import type { Partner } from '@chancenwerk/master-data-management/partner/entities/Partner.js';
import type { School } from '@chancenwerk/master-data-management/partner/entities/School.js';
import { KPFPublicInformationAPI } from '@chancenwerk/public-access/api/KPFPublicInformationAPI.js';
import { html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { g_getProductCategoryString } from '../helper.js';

/**
 * @customElement logistik-component-edit-chancen-brief-variables
 */
@customElement('logistik-component-edit-chancen-brief-variables')
export class LogistikComponentEditChancenBriefVariables extends ChemistryLitElement {
  @property({ attribute: false }) product?: ChancenBrief | TravelCertificate;
  @property({ attribute: false }) orderItem?: LogistikOrderChancenBriefItem | LogistikCartItem;

  @property({ type: Boolean }) showEmptyError = false;

  override render() {
    if (!this.product || !this.orderItem) {
      return this._renderPlaceholder();
    }

    return html`
      <cr-flex space="md">
        <cr-flex horizontal space-between space="md">
          ${this._renderSchoolPicker()}
          <cr-button
            mode="outline"
            icon="print"
            @click=${() => {
              // if (!assertVariablesForChancenBrief(this.product, this.orderItem)) return;
              if (!this.product) return;
              goTo(
                this.product.getGeneratedPDFLink(
                  this.product.flyaVariables
                    ?.map(
                      chancenBriefVar =>
                        new ChancenBriefVariableValue({
                          variableName: chancenBriefVar.variableName,
                          variableValue: chancenBriefVar.variableName,
                        }),
                    )
                    .map(chancenBriefVarValue => {
                      const orderItemVarVal = (this.orderItem?.flyaVariableValues ?? []).find(
                        orderVarValue => orderVarValue.variableName === chancenBriefVarValue.variableName,
                      );
                      if (orderItemVarVal) chancenBriefVarValue.variableValue = orderItemVarVal.variableValue;
                      return chancenBriefVarValue;
                    }) ?? [],
                ),
                { target: '_blank' },
              );
            }}
          >
            Vorschau
          </cr-button>
        </cr-flex>
        <cr-grid space="sm" columns="max-content 1fr">
          ${repeat(this.product.flyaVariables ?? [], chancenbriefVariable => {
            const value = this.orderItem?.flyaVariableValues?.find(
              varValue => varValue.variableName === chancenbriefVariable.variableName,
            )?.variableValue;
            const showEmptyError = !value && this.showEmptyError;
            return html`
              <cr-flex center> <cr-text bold tone="subtle"> ${chancenbriefVariable.variableName}* </cr-text> </cr-flex>

              <cr-input
                placeholder=${chancenbriefVariable.variableName}
                @value-picked=${(e: ValueEvent<string>) => {
                  this._setValueOnVariable(chancenbriefVariable.variableName, e.detail.value);
                  this.dispatchEvent(new CustomEvent('value-picked', { detail: { value: this.orderItem } }));
                  this.requestUpdate();
                }}
                @value-changed=${(e: ValueEvent<string>) => {
                  this._setValueOnVariable(chancenbriefVariable.variableName, e.detail.value);
                  this.dispatchEvent(new CustomEvent('value-changed', { detail: { value: this.orderItem } }));
                  this.requestUpdate();
                }}
                .value=${this.orderItem?.flyaVariableValues?.find(
                  varValue => varValue.variableName === chancenbriefVariable.variableName,
                )?.variableValue ?? ''}
                .info=${possiblePrefillChancenBriefVariableNames.includes(
                  chancenbriefVariable.variableName as (typeof possiblePrefillChancenBriefVariableNames)[number],
                )
                  ? 'Dieses Feld kann automatisch ausgefüllt werden'
                  : ''}
                .error=${showEmptyError ? 'Dieses Feld muss ausgefüllt werden' : null}
              >
              </cr-input>
            `;
          })}
        </cr-grid>
      </cr-flex>
    `;
  }

  private _renderPlaceholder() {
    return html`
      <cr-empty-placeholder
        .text=${this.product
          ? `Kein ${g_getProductCategoryString(this.product._class)}`
          : 'Kein CHANCENbrief / CHANCENreise-Zertifikat'}
        .img=${'close'}
      ></cr-empty-placeholder>
    `;
  }

  private _renderSchoolPicker() {
    if (!this.product) {
      return nothing;
    }
    return html`<chancen-component-public-school-picker
      flex
      placeholder="Standort für automatisches Ausfüllen"
      emptyState="Keine Standorte gefunden"
      @value-picked=${async (e: ValueEvent<Partner>) => this._prefillVariablesFromSchool(e.detail.value as School)}
    >
      <cr-tooltip
        .text=${html`
          <cr-text>
            Hier können Sie Ihren Standort auswählen um einige Felder für die
            ${g_getProductCategoryString(this.product._class)} Generierung vorauszufüllen
          </cr-text>
        `}
      ></cr-tooltip>
    </chancen-component-public-school-picker>`;
  }

  private _setValueOnVariable(variableName: string, newValue: string) {
    let flyaVariableValue = this.orderItem?.flyaVariableValues?.find(
      varValue => varValue.variableName === variableName,
    );
    if (!flyaVariableValue) {
      flyaVariableValue = new ChancenBriefVariableValue({ variableName });
      if (this.orderItem && !this.orderItem?.flyaVariableValues) this.orderItem.flyaVariableValues = [];
      this.orderItem?.flyaVariableValues?.push(flyaVariableValue);
    }

    flyaVariableValue.variableValue = newValue;
  }

  private async _prefillVariablesFromSchool(school: School) {
    const kpfs = await KPFPublicInformationAPI.getBySchoolID([school._id])({
      // @ts-expect-error durchfuerungsTage is correctly specified with allFields, switching to selectionSet function does not yield the same results
      produkt: { durchfuehrungsTage: DurchfuehrungsTag.allFields },
      schukoTel: 1,
      email: 1,
      schuKos: { vorname: 1, nachname: 1, email: 1 },
      personsOperativ: { email: 1, vorname: 1, nachname: 1 },
    });

    const kpf = kpfs.find(kpf => kpf.produkt instanceof ProductLKK);
    const durchfuehrungsTage = (kpf?.produkt as Maybe<ProductLKK>)?.durchfuehrungsTage ?? [];

    const variableValues: Partial<Record<(typeof possiblePrefillChancenBriefVariableNames)[number], string>> = {
      Schule_Name: school.name ?? '',
      Schule_Strasse: school.address?.strasse ?? '',
      Schule_PLZ: school.address?.zipCity?.zip ?? '',
      Schule_Stadt: school.address?.zipCity?.city ?? '',
      Schule_Kuerzel: school.kuerzel ?? '',
      // Schuko
      Mobilnummer: kpf?.schukoTel ?? '',
      email1: kpf?.email ?? kpf?.schuKos?.at(0)?.email ?? '',
      Name_Schulkoordination: `${kpf?.schuKos?.at(0)?.vorname ?? ''} ${kpf?.schuKos?.at(0)?.nachname ?? ''}`.trim(),
      // AP intern operativ
      Name: `${kpf?.personsOperativ?.at(0)?.vorname ?? ''} ${kpf?.personsOperativ?.at(0)?.nachname ?? ''}`.trim(),
      email2: kpf?.personsOperativ?.at(0)?.email ?? '',
      Raumnummer: durchfuehrungsTage
        .flatMap(d => d.locations)
        .filter((v, i, vs) => vs.indexOf(v) === i)
        .join(', '),
      ...Object.fromEntries(
        durchfuehrungsTage.flatMap((durchfuehrungsTag, i) => [
          [`Wochentag_${i + 1}`, durchfuehrungsTag.wochentag],
          [
            `Lernförderzeit_${i + 1}`,
            `${durchfuehrungsTag.startZeit?.toFormat('HH:mm')}-${durchfuehrungsTag.endZeit?.toFormat('HH:mm')} Uhr`,
          ],
        ]),
      ),
    };
    this.product?.flyaVariables?.forEach(variable => {
      if (variable.variableName) {
        const varName = variable.variableName as (typeof possiblePrefillChancenBriefVariableNames)[number];
        if (possiblePrefillChancenBriefVariableNames.includes(varName) && variableValues[varName] !== undefined)
          this._setValueOnVariable(variable.variableName, variableValues[varName]!);
      }
    });

    this.requestUpdate();
  }
}
